import Dialog from "@components/Dialog"
import DownloadImageForm, {
  DownloadImageFormValues,
} from "@features/results/DownloadChartImageDialog/DownloadImageForm"
import { createWatermarkedChartImage } from "@features/results/DownloadChartImageDialog/helpers"
import { CameraAlt } from "@mui/icons-material"
import { Box, BoxProps, Button, IconButton, IconButtonProps, Tooltip } from "@mui/material"
import logger from "@utils/logger"
import { Ref, useState } from "react"
import { downloadHtmlImage } from "utils/file"
import { enqueueSnackbar } from "notistack"

type DownloadChartImageDialogProps = {
  defaultFileName: string
  chartRef: Ref<HTMLDivElement>
  buttonProps?: IconButtonProps
  testId?: string
  buttonContainerProps?: BoxProps
  runReference: string
}

const FORM_ID = "download-image-form"

const DownloadChartImageDialog = ({
  defaultFileName,
  buttonProps,
  testId,
  buttonContainerProps,
  chartRef,
  runReference,
}: DownloadChartImageDialogProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const [isProcessingImage, setIsProcessingImage] = useState(false)

  const handleClose = () => setIsOpen(false)

  const handleFormSubmit = async (formValues: DownloadImageFormValues) => {
    const refElement = typeof chartRef === "object" && chartRef !== null ? chartRef.current : null

    if (!refElement) {
      logger.error("Chart DOM reference element is not available")
      return
    }

    setIsProcessingImage(true)

    try {
      const chartWithWatermark = await createWatermarkedChartImage(refElement, runReference)

      document.body.append(chartWithWatermark)

      setTimeout(async () => {
        await downloadHtmlImage(formValues.name, chartWithWatermark, 4)
        setIsProcessingImage(false)
        document.body.removeChild(chartWithWatermark)
        handleClose()
      })
    } catch (error) {
      enqueueSnackbar("An error occurred while creating the image", { variant: "error" })
      logger.error("error during creation of chart image", error)
      setIsProcessingImage(false)
    }
  }

  return (
    <>
      <Box {...buttonContainerProps} sx={{ display: "inline-block", ...buttonContainerProps?.sx }}>
        <Tooltip title="Download chart image">
          <span>
            <IconButton data-testid={testId} onClick={() => setIsOpen(true)} {...buttonProps}>
              <CameraAlt color="primary" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        title="Save chart image"
        aria-labelledby="Save chart image dialog"
        aria-describedby="Save chart image dialog"
        sx={{ ".MuiDialogContent-root": { minWidth: "400px" } }}
        actionsComponent={
          <>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" form={FORM_ID} disabled={isProcessingImage}>
              Save
            </Button>
          </>
        }
      >
        <DownloadImageForm
          defaultValues={{ name: defaultFileName }}
          onSubmit={handleFormSubmit}
          id={FORM_ID}
        />
      </Dialog>
    </>
  )
}

export default DownloadChartImageDialog
